import { ChartCard } from "@/components/ChartCard";
import { DeepLink } from "@/components/DeepLink";
import { SortableTable, type ColumnDef } from "@/components/SortableTable";
import { ModalShell, Pill } from "@/components/ui";
import { t } from "@/i18n";
import { fmtDateShort, fmtDateTime, fmtInt, fmtMoney } from "@/lib/utils";

import {
  fmtSure,
  useTeslimatHafta,
  type TeslimatHafta,
  type TeslimatHaftaSecimi,
  type TeslimatSiparis,
} from "./teslimat";

/**
 * Haftalık teslimat grafiği — diğer grafiklerle AYNI altyapı (`ChartCard`,
 * Chart.js): gerçek tooltip (hafta, ortalama, sipariş adedi), tıklanınca o
 * haftanın modali, imleç el. Önceki elle çizilmiş SVG `preserveAspectRatio=
 * "none"` ile yatay esnetiliyor (metinler bozuluyor), nokta etiketleri
 * çizgiyle çakışıyor ve tooltip yoktu (17.09.2026 kullanıcı bildirimi).
 *
 * Klavye erişimi: canvas odaklanamaz; grafiğin altında `sr-only` bir hafta
 * listesi vardır — Tab ile gelince görünür olur (`focus-within:not-sr-only`).
 * jsdom'da canvas olmadığından testler `@/components/ChartCard`'ı sahteler.
 */

const SANIYE_GUN = 86400;
/** x ekseninde en fazla bu kadar tarih etiketi gösterilir (mobilde okunur kalsın). */
const MAKS_X_ETIKET = 8;

function gunEtiket(gun: number): string {
  return `${gun.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}g`;
}

interface TeslimatChartProps {
  haftalar: TeslimatHafta[];
  onHaftaSec: (secim: TeslimatHaftaSecimi) => void;
}

/** Haftalık ortalama teslimat süresi — nokta = hafta, tıklanınca modal açılır. */
export function TeslimatChart({ haftalar, onHaftaSec }: TeslimatChartProps) {
  const labels = haftalar.map((h) => fmtDateShort(h.hafta_baslangic));
  const gunler = haftalar.map((h) => (h.ort_saniye != null && h.ort_saniye > 0 ? h.ort_saniye / SANIYE_GUN : null));
  const labelEvery = Math.max(1, Math.ceil(haftalar.length / MAKS_X_ETIKET));

  function secimYap(i: number) {
    const h = haftalar[i];
    if (h) onHaftaSec({ yil: h.yil, hafta_no: h.hafta_no });
  }

  const haftaEtiketi = (i: number) => {
    const h = haftalar[i];
    return h ? `${t("ozet.teslimat.hafta_baslik", { n: String(h.hafta_no) })} · ${fmtDateShort(h.hafta_baslangic)}` : "";
  };

  return (
    <div role="group" aria-label={t("ozet.teslimat.hafta_aria")} data-testid="teslimat-chart">
      <ChartCard
        bare
        type="line"
        height={240}
        labelEvery={labelEvery}
        data={{
          labels,
          datasets: [{ label: t("ozet.teslimat.ortalama_seri"), data: gunler, pointRadius: 4, pointHoverRadius: 7 }],
        }}
        format={{ tick: gunEtiket, tooltip: (gun) => fmtSure(gun * SANIYE_GUN, true) }}
        tooltipTitle={haftaEtiketi}
        tooltipLines={(i) => (haftalar[i] ? [t("ozet.teslimat.siparis_adet", { n: fmtInt(haftalar[i].adet) })] : [])}
        onPointClick={secimYap}
      />
      <ul className="sr-only mt-2 flex flex-wrap gap-1.5 focus-within:not-sr-only" aria-label={t("ozet.teslimat.hafta_listesi")}>
        {haftalar.map((h, i) => (
          <li key={`${h.yil}-${h.hafta_no}`}>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => secimYap(i)}>
              {haftaEtiketi(i)} · {fmtSure(h.ort_saniye)}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface TeslimatIstatistikCipleriProps {
  genelOrtSaniye: number | null;
  olculenSiparis: number;
  sonHafta: TeslimatHafta | undefined;
  veriKapsamiPct: number;
}

/** Grafiğin üstündeki istatistik çipleri — eski `app.js:900-906`. */
export function TeslimatIstatistikCipleri({
  genelOrtSaniye,
  olculenSiparis,
  sonHafta,
  veriKapsamiPct,
}: TeslimatIstatistikCipleriProps) {
  return (
    <div className="flex flex-wrap gap-x-8 gap-y-3">
      <div>
        <p className="text-xs text-muted">{t("ozet.teslimat.genel_ortalama")}</p>
        <p className="num text-xl font-bold text-ink">{fmtSure(genelOrtSaniye, true)}</p>
        <p className="text-xs text-muted">{t("ozet.teslimat.teslim_olculdu", { n: fmtInt(olculenSiparis) })}</p>
      </div>
      {sonHafta && (
        <div>
          <p className="text-xs text-muted">{t("ozet.teslimat.son_hafta")}</p>
          <p className="num text-xl font-bold text-ink">{fmtSure(sonHafta.ort_saniye, true)}</p>
          <p className="text-xs text-muted">{t("ozet.teslimat.siparis_adet", { n: fmtInt(sonHafta.adet) })}</p>
        </div>
      )}
      {veriKapsamiPct < 95 && (
        <div>
          <p className="text-xs text-muted">{t("ozet.teslimat.veri_kapsami")}</p>
          <p className="num text-xl font-bold text-ink">{`%${veriKapsamiPct}`}</p>
          <p className="text-xs text-muted">{t("ozet.teslimat.veri_kapsami_sub")}</p>
        </div>
      )}
    </div>
  );
}

const surePill = (r: TeslimatSiparis) => {
  const s = Number(r.sure_saniye) || 0;
  const saat = s / 3600;
  const tone = saat <= 48 ? "ok" : saat <= 96 ? "warn" : "bad";
  return <Pill tone={tone}>{fmtSure(r.sure_saniye, true)}</Pill>;
};

const columns: ColumnDef<TeslimatSiparis>[] = [
  {
    key: "order_no",
    header: t("ozet.teslimat.kolon.siparis"),
    accessor: (r) => <DeepLink href={r.admin_link} label={r.order_no || String(r.siparis_id ?? "—")} />,
  },
  { key: "customer_name", header: t("ozet.teslimat.kolon.musteri"), accessor: (r) => r.customer_name || "—" },
  {
    key: "amount_try",
    header: t("ozet.teslimat.kolon.tutar"),
    accessor: (r) => fmtMoney(r.amount_try),
    sortValue: (r) => Number(r.amount_try) || 0,
  },
  {
    key: "siparis_tarihi",
    header: t("ozet.teslimat.kolon.siparis_ani"),
    accessor: (r) => fmtDateTime(r.siparis_tarihi),
    sortValue: (r) => r.siparis_tarihi || "",
  },
  {
    key: "teslim_tarihi",
    header: t("ozet.teslimat.kolon.teslim_ani"),
    accessor: (r) => fmtDateTime(r.teslim_tarihi),
    sortValue: (r) => r.teslim_tarihi || "",
  },
  {
    key: "sure_saniye",
    header: t("ozet.teslimat.kolon.gecen_sure"),
    accessor: surePill,
    sortValue: (r) => Number(r.sure_saniye) || 0,
  },
  { key: "cargo_company", header: t("ozet.teslimat.kolon.kargo"), accessor: (r) => r.cargo_company || "—" },
];

interface TeslimatHaftaModalProps {
  secim: TeslimatHaftaSecimi | null;
  onClose: () => void;
}

/** Bir haftaya tıklayınca açılan detay modalı — süreye göre sıralanabilir tablo + Ticimax linki. */
export function TeslimatHaftaModal({ secim, onClose }: TeslimatHaftaModalProps) {
  const query = useTeslimatHafta(secim);
  if (!secim) return null;

  const baslik = query.data
    ? t("ozet.teslimat.modal_baslik", { n: String(query.data.hafta), adet: String(query.data.adet) })
    : t("ozet.teslimat.hafta_baslik", { n: String(secim.hafta_no) });

  return (
    <ModalShell open title={baslik} onClose={onClose} size="lg">
      {query.isLoading && <p className="text-sm text-muted">{t("common.loading")}</p>}
      {query.isError && (
        <p role="alert" className="text-sm text-red">
          {t("common.error")}
        </p>
      )}
      {query.data && (
        <div className="space-y-2">
          <p className="text-xs text-muted">{t("ozet.teslimat.ortalama_not", { deger: fmtSure(query.data.ort_saniye, true) })}</p>
          <SortableTable
            columns={columns}
            rows={query.data.siparisler}
            rowKey={(r) => String(r.siparis_id ?? r.order_no ?? "")}
            initialSortKey="sure_saniye"
          />
        </div>
      )}
    </ModalShell>
  );
}
